import React, { useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { LocalShipping as DispatchIcon } from '@mui/icons-material';
import {
  EasyCheckDialog,
  EasyCheckButton,
  EasyCheckTextField,
  EasyCheckDatePicker,
} from '../../common/Controls';
import { useDispatchCourier } from '../../../hooks/useChequeCollections';

const CourierDispatchDialog = ({ open, cheque, onClose, onSuccess, onError }) => {
  const dispatchMutation = useDispatchCourier();

  const [form, setForm] = useState({
    dispatchDate: new Date(),
    courierCompany: '',
    driverName: '',
    trackingReference: '',
    expectedDeliveryDate: null,
    remarks: '',
  });

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.courierCompany.trim()) {
      onError('Courier company is required');
      return;
    }
    if (!form.driverName.trim()) {
      onError('Driver name is required');
      return;
    }
    if (!form.trackingReference.trim()) {
      onError('Tracking reference is required');
      return;
    }

    const chequeId = cheque.ChequeId || cheque.chequeId;
    try {
      const result = await dispatchMutation.mutateAsync({
        chequeId,
        dispatchDate: form.dispatchDate?.toISOString() || new Date().toISOString(),
        courierCompany: form.courierCompany.trim(),
        driverName: form.driverName.trim(),
        trackingReference: form.trackingReference.trim(),
        expectedDeliveryDate: form.expectedDeliveryDate?.toISOString().split('T')[0] || null,
        remarks: form.remarks.trim() || null,
      });
      if (result.success) {
        onSuccess(result.message || 'Courier dispatch recorded successfully');
      } else {
        onError(result.message || 'Failed to record dispatch');
      }
    } catch (err) {
      onError('Failed to record dispatch');
    }
  };

  const chequeNumber = cheque?.ChequeNumber || cheque?.chequeNumber || '';
  const beneficiary = cheque?.BeneficiaryName || cheque?.beneficiaryName || '';

  return (
    <EasyCheckDialog
      open={open}
      onClose={onClose}
      title="Courier Dispatch"
      variant="form"
      titleIcon={<DispatchIcon />}
      actions={
        <>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={onClose}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            onClick={handleSave}
            loading={dispatchMutation.isPending}
          >
            Dispatch
          </EasyCheckButton>
        </>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cheque <strong>{chequeNumber}</strong> — {beneficiary}
      </Typography>

      <Stack spacing={2}>
        <EasyCheckDatePicker
          label="Dispatch Date"
          value={form.dispatchDate}
          onChange={handleChange('dispatchDate')}
          slotProps={{ textField: { required: true, fullWidth: true } }}
        />

        <EasyCheckTextField
          label="Courier Company"
          value={form.courierCompany}
          onChange={handleChange('courierCompany')}
          required
          fullWidth
          placeholder="e.g. Aramex, DHL, Company Driver"
        />

        <EasyCheckTextField
          label="Driver / Agent Name"
          value={form.driverName}
          onChange={handleChange('driverName')}
          required
          fullWidth
          placeholder="Name of courier person"
        />

        <EasyCheckTextField
          label="Tracking Reference"
          value={form.trackingReference}
          onChange={handleChange('trackingReference')}
          required
          fullWidth
          placeholder="Waybill or tracking number"
        />

        <EasyCheckDatePicker
          label="Expected Delivery Date"
          value={form.expectedDeliveryDate}
          onChange={handleChange('expectedDeliveryDate')}
          slotProps={{ textField: { fullWidth: true } }}
        />

        <EasyCheckTextField
          label="Remarks"
          value={form.remarks}
          onChange={handleChange('remarks')}
          fullWidth
          multiline
          rows={2}
          placeholder="Any notes (optional)"
        />
      </Stack>
    </EasyCheckDialog>
  );
};

export default CourierDispatchDialog;
